import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BasicDatatable from "../Common/BasicDatatable";
import CustomConfirm from "../../components/Common/CustomConfirm";
import ActionItem from "../ActionItems/ActionItem";
import {
  getColumns,
  initialSort,
  initialFilter,
} from "./InteractionActionTableConfig";
import useGlobalContext from "../../context/useGlobalContext";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { formatDateOnly } from "../../utils/utils";
import { useLocation } from "react-router-dom";
import { GlobalContext } from "../../context/GlobalContext";
import ActionItemService from "../../services/actionItems/ActionItemService";
import SlideOver from "../Common/SlideOver";
import "../../styles/interactionDetailsPage.css";
import FeedbackPopup from "../Common/FeedbackPopUp";
import UserSelector from "../ActionItems/UserSelector";
import AssigneeIcon from "../../assets/action-items/assignee-icon.svg";
import DueDateIcon from "../../assets/action-items/due-date-calendar.svg";
import CommonDatePicker from "../Common/CommonDatePicker";
import OpenTaskIcon from "../../assets/common/Open-task-icon.svg";
import CloseTaskIcon from "../../assets/common/Close-task-icon.svg";
import { ActionItemSelectedType, ConvertTaskAction } from "../../utils/types";
import {
  patchConvertedActionItemRows,
  resolveIsSubTaskFlag,
} from "../../utils/subtaskUtils";
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';

const generateRows = (actionItems: any, actionItemRowDetails: any, activeUsers?: any[]): any[] =>
  (actionItems || [])
    .filter(
      (item: any) => item?.isSubTask !== true
    )
    .map((item: any) => mapActionItemRow(item, actionItemRowDetails, activeUsers));

const mapActionItemRow = (
  item: any,
  actionItemRowDetails: any,
  activeUsers?: any[],
): any => {
  const isSubTask = resolveIsSubTaskFlag(item);
  const nestedSubTasks =
    !isSubTask && Array.isArray(item?.subTasks) ? item.subTasks : [];
  const childRows = nestedSubTasks.map((subTask: any) =>
    mapActionItemRow(
      {
        ...subTask,
        parentActionItemId: subTask.parentActionItemId ?? item.actionItemId,
        parentActionItemTitle: subTask.parentActionItemTitle ?? item.title,
        parentDueDate: item.dueDate ?? null,
        parentActionItem: {
          ...(subTask.parentActionItem || {}),
          dueDate: item.dueDate ?? subTask.parentActionItem?.dueDate,
        },
      },
      actionItemRowDetails,
      activeUsers,
    )
  );
  return {
    id: item.actionItemId,
    description: item.title,
    meeting_id: item.interactionId ?? item.meeting_id,
    interactionId: item.interactionId,
    interactionTitle: item.interactionTitle,
    summary: item.summary ?? item.interactionTitle,
    dueDate: item.dueDate,
    revised_due_date: item.revisedDueDate,
    assignee: item.assigneeEmail,
    assignee_name: item.assigneeName,
    assigneePorId: item.assigneePorId,
    status: item.status,
    attachments: item.attachments,
    creator: item.creatorEmail,
    creator_name: item.creatorName,
    creator_email: item.creatorEmail,
    is_ai_generated: item?.isAiGenerated,
    client_domain: (item?.client_domain as string) ?? "Internal",
    topic: item.topic,
    isTask: item?.isTask ?? false,
    canEdit: item?.isDeleted || item?.isDiscarded ? false : item?.canEdit ?? false,
    coachEmail: item?.coachEmail ?? null,
    priority: item?.priority,
    actionItemRowDetails: actionItemRowDetails,
    activeUsers: activeUsers,
    isDiscarded: item?.isDeleted ? true : item?.isDiscarded,
    isApproved: item?.isTask === true,
    domainTopic: item?.domainTopic ?? item?.domain_topic ?? null,
    isSubTask,
    parentActionItemId: item?.parentActionItemId ?? null,
    parentActionItemTitle: item?.parentActionItemTitle,
    parentDueDate: item?.parentDueDate ?? null,
    parentActionItem: item?.parentActionItem,
    subTasks: isSubTask ? undefined : childRows.length ? childRows : undefined,
    subRows: childRows.length ? childRows : undefined,
  };
};

const flattenActionItemRows = (list: any[] = []): any[] =>
  list.flatMap((row) => [
    row,
    ...flattenActionItemRows(row?.subRows || row?.subTasks || []),
  ]);

const getActionItemRowData = (row: any) => row?.original ?? row ?? {};

const isManualActionItem = (row: any) => {
  const data = getActionItemRowData(row);
  return (data.isAiGenerated ?? data.is_ai_generated) === false;
};

const isAcceptedActionItem = (row: any) => {
  const data = getActionItemRowData(row);
  return data.isTask === true || data.isApproved === true;
};

// Pending AI items: not manual, not already accepted, and not discarded.
const isNoActionTaken = (row: any) => {
  const data = getActionItemRowData(row);
  if (data.isSubTask) return false;
  if (isManualActionItem(data)) return false;
  if (isAcceptedActionItem(data)) return false;
  if (data.isDiscarded) return false;
  return true;
};

interface ActionItemsProps {
  id: string;
  meetingDate?: string;
  attendees?: any;
  isInteractionAttendee?: boolean;
  interactionDetails?: any;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  openDialog: boolean;
  setIsNewActionItem?: React.Dispatch<React.SetStateAction<boolean>>;
  isNewActionItem?: boolean;
  activeUsers?: any[];
  assetId?: any;
  onGroupingPresenceChange?: (hasGrouping: boolean) => void;
  selectedType?: ActionItemSelectedType;
  onCountChange?: (external: any, internal: any) => void
  bulkTasks?: boolean;
  setBulkTasks?: React.Dispatch<React.SetStateAction<boolean>>;
}
const ActionItemsPage: React.FC<ActionItemsProps> = ({
  id,
  meetingDate,
  attendees,
  setOpenDialog,
  openDialog,
  setIsNewActionItem,
  isNewActionItem,
  activeUsers,
  assetId,
  onGroupingPresenceChange,
  selectedType = "external",
  onCountChange,
  bulkTasks,
  setBulkTasks,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const is_team_meeting_id = true;
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { showSnackbar } = useDisplaySnackbar();
  const [actionItemToBeUpdated, setActionItemToBeUpdated] =
    useState<string>("");
  const [actionItemToDelete, setActionItemToDelete] = useState<any | null>(
    null
  );

  const isLargeUp = useMediaQuery(theme.breakpoints.up("lg"));
  const [columns, setColumns] = useState<any[]>([]);

  const [selectedActionItem, setSelectedActionItem] = useState<any | null>(
    null
  );
  const { accountDetails, impersonationDetails } = useContext(GlobalContext);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const selectedTabs = location.state?.selectedTab ?? params.get("st") ?? "";
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const sharedWithMe = location.state?.sharedWithMe ?? selectedTabs === "sharedWithMe"? true : false;
  const sharedByMe = location.state?.sharedByMe ?? selectedTabs === "sharedByMe"? true : false;
  const [rows, setRows] = useState<any[]>([]);
  const [selectedActionItemIds, setSelectedActionItemIds] = useState<string[]>(
    []
  );
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [discardTrigger, setDiscardTrigger] = useState(false);
  const handleSave = () => setSaveTrigger(true);
  const handleDiscard = () => {(selectedActionItem === null && setDiscardTrigger(true)), setOpenDialog(false);};
  const profileOrgId = accountDetails?.profileOrgId ?? "";
  const [isActionItemValid, setIsActionItemValid] = useState(false);
  const [actionItemRowDetails, setActionItemRowDetails] = useState<any>(null);
  const [highlightedCells, setHighlightedCells] = useState<Record<string, any>>(
    {}
  );
  const [expandedRowIds, setExpandedRowIds] = useState<Record<string, boolean>>(
    {}
  );

  const [discardPopupOpen, setDiscardPopupOpen] = useState(false);
  const [feedbackReference, setFeedbackReference] = useState<string>("");
  const [discardFeedbackAnchorEl, setDiscardFeedbackAnchorEl] =
    useState<HTMLElement | null>(null);
  const [discardFeedbackRowId, setDiscardFeedbackRowId] = useState<string>("");
  const { featureMap } = useGlobalContext();
  const userFeedback = featureMap?.idp_user_feedback === true;
  const [bulkAssigneeId, setBulkAssigneeId] = useState<string | null>(null);
  const [bulkDueDate, setBulkDueDate] = useState<string | null>(null);
  const [bulkDueDateAnchorEl, setBulkDueDateAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedDomainTopic, setExpandedDomainTopic] = useState<string | false>(false);
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;
  const selectedRows = useMemo(
    () =>
      flattenActionItemRows(rows).filter((row) =>
        selectedActionItemIds.includes(row.id)
      ),
    [rows, selectedActionItemIds]
  );
  const isSubtaskBulkSelection =
    selectedRows.length > 0 && selectedRows.every((row) => row.isSubTask);
  const hasSelectedSubtask = selectedRows.some((row) => row.isSubTask);
  const hasSelectedParent = selectedRows.some((row) => !row.isSubTask);
  const rowsEligibleForAcceptDiscard = selectedRows.filter(isNoActionTaken);
  const showBulkAcceptDiscard =
    !isSubtaskBulkSelection && rowsEligibleForAcceptDiscard.length > 0;

  const getHiddenSelectRowIds = (tableRows: any[]) =>
    flattenActionItemRows(tableRows)
      .filter((row) => {
        if (hasSelectedSubtask && !row.isSubTask) return true;
        if (hasSelectedParent && row.isSubTask) return true;
        return false;
      })
      .map((row) => row.id);

  const handleBulkSelectionChange = (ids: string[]) => {
    const nextSelected = flattenActionItemRows(rows).filter((row) =>
      ids.includes(row.id)
    );
    const parentIds = nextSelected
      .filter((row) => !row.isSubTask)
      .map((row) => row.id);
    const subtaskIds = nextSelected
      .filter((row) => row.isSubTask)
      .map((row) => row.id);
    const mixesParentAndSubtask = parentIds.length > 0 && subtaskIds.length > 0;
    if (mixesParentAndSubtask) {
      const addedIds = ids.filter((id) => !selectedActionItemIds.includes(id));
      const addedSubtasks = addedIds.filter((id) => subtaskIds.includes(id));
      // Select-all includes children; keep parents and drop subtasks.
      if (addedIds.length > 1) {
        setSelectedActionItemIds(parentIds);
        return;
      }
      if (addedSubtasks.length > 0) {
        setSelectedActionItemIds(subtaskIds);
        return;
      }
      setSelectedActionItemIds(parentIds);
      return;
    }
    setSelectedActionItemIds(ids);
  };
  const bulkAssignee = useMemo(
    () =>
      activeUsers?.find(
        (user) =>
          user?.profileOrgRelationshipId === bulkAssigneeId
      ) ?? null,
    [activeUsers, bulkAssigneeId]
  );
  const assigneeRef = useRef<HTMLDivElement>(null);

  const sortActionItems = (list: any[]) => {
    return [...list]
      .filter(item => !(item.isTask === true && item.isDeleted === true)) // HIDE ROWS when is_task and is_deleted both are true
      .sort((a, b) => {
        if (a.isDeleted && !b.isDeleted) return 1;
        if (!a.isDeleted && b.isDeleted) return -1;

        if (a.isAiGenerated === false && b.isAiGenerated !== false) return -1;
        if (a.isAiGenerated !== false && b.isAiGenerated === false) return 1;

        if (a.isTask === true && b.isTask !== true) return -1;
        if (a.isTask !== true && b.isTask === true) return 1;

        if (a.isTask === false && b.isTask !== false) return -1;
        if (a.isTask !== false && b.isTask === false) return 1;

        return 0;
      });
  };

  const {
    isLoading: isMeetingActionLoading,
    isFetching: isMeetingActionFetching,
    isSuccess: isMeetingActionSuccess,
    isError: isMeetingActionError,
    error: MeetingActionError,
    data: MeetingActionItemData,
  } = ActionItemService.useGetActionItems(
    {
      interactionId: id || "",
      isTeamInteraction: is_team_meeting_id,
      sharedWithMe: sharedWithMe || undefined,
      sharedByMe: sharedByMe || undefined,
      assetId: assetId,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    true
  );

  const hasAnyDomainTopic = useMemo(() => {
    const responseData = MeetingActionItemData?.data?.data || {};

    const allItems = [
      ...(responseData?.external || []),
      ...(responseData?.internal || []),
    ];

     onCountChange?.(
      responseData?.external?.length || 0,
      responseData?.internal?.length || 0
    );

    return allItems.some((item: any) => {
      const value = String(
        item?.domainTopic ?? item?.domain_topic ?? ""
      ).trim();

      return value.length > 0;
    });
  }, [MeetingActionItemData]);

  useEffect(() => {
    onGroupingPresenceChange?.(hasAnyDomainTopic);
  }, [hasAnyDomainTopic, onGroupingPresenceChange]);

  const groupedRowsByDomainTopic = useMemo(() => {
    if (!hasAnyDomainTopic) return [];

    const OTHERS_KEY = "__others__";
    const groups = new Map<string, { key: string; label: string; rows: any[] }>();

    const ensureGroup = (key: string, label: string) => {
      if (!groups.has(key)) groups.set(key, { key, label, rows: [] });
      return groups.get(key)!;
    };

    rows.forEach((row) => {
      const raw = String(row?.domainTopic ?? "").trim();
      const normalized = raw.toLowerCase();
    
      const isOthersGroup =
        raw.length === 0 ||
        normalized === "other" ||
        normalized === "others";
    
      const key = isOthersGroup ? OTHERS_KEY : raw;
      const label = isOthersGroup ? "Other" : raw;
    
      ensureGroup(key, label).rows.push(row);
    });

    const list = Array.from(groups.values());
    return list.sort((a, b) => {
      if (a.key === OTHERS_KEY && b.key !== OTHERS_KEY) return 1;
      if (a.key !== OTHERS_KEY && b.key === OTHERS_KEY) return -1;
      return 0;
    });
  }, [rows, hasAnyDomainTopic]);

  useEffect(() => {
    if (!hasAnyDomainTopic) {
      setExpandedDomainTopic(false);
      return;
    }

    // Default expand: first non-"Others" group if present, otherwise "Others"
    const firstGroupKey =
      groupedRowsByDomainTopic.find((g) => g.key !== "__others__")?.key ??
      groupedRowsByDomainTopic[0]?.key ??
      false;
    setExpandedDomainTopic(firstGroupKey);
  }, [hasAnyDomainTopic, groupedRowsByDomainTopic.length]);

  const highlightMissingFields = (
    row: any,
    fields: Record<string, boolean>,
    autoOpen = true,
  ) => {
    setHighlightedCells((prev) => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        ...fields,
        open_assignee: autoOpen && fields.assignee,
        autoOpenDueDatePicker: autoOpen && fields.dueDate,
      },
    }));
  };

  const removeHighlight = (rowId: string, field: string) => {
    setHighlightedCells((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [field]: false,
      },
    }));
  };  
  const clearHighlightForRow = (rowId: string) => {
    setHighlightedCells((prev) => {
      const newState = { ...prev };
      delete newState[rowId];
      return newState;
    });
  };  

  const clearDiscardFeedbackState = () => {
    setDiscardPopupOpen(false);
    setFeedbackReference("");
    setDiscardFeedbackAnchorEl(null);
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === discardFeedbackRowId
          ? { ...row, isDiscardFeedbackActive: false }
          : row
      )
    );
    setDiscardFeedbackRowId("");
  };

  const getRowData = (row: any) => row?.original ?? row ?? null;
  const patchSelectedRows = (updates: Record<string, any>, perRow?: (row: any) => Record<string, any>) => {
    const patchTree = (list: any[]): any[] =>
      list.map((row) => {
        const isSelected = selectedActionItemIds.includes(row.id);
        const nextUpdates = isSelected
          ? { ...updates, ...(perRow ? perRow(row) : {}) }
          : {};
        let nextRow = isSelected ? { ...row, ...nextUpdates } : row;
        const nested = row.subRows ? patchTree(row.subRows) : undefined;
        if (!nested) return nextRow;
        return { ...nextRow, subRows: nested, subTasks: nested };
      });
    setRows((prevRows) => patchTree(prevRows));
  };

  useEffect(() => {
    setSelectedActionItemIds((prev) =>
      prev.filter((selectedId) => rows.some((row) => row.id === selectedId))
    );
  }, [rows]);

  useEffect(() => {
    if (selectedActionItemIds.length === 0) {
      setBulkAssigneeId(null);
      setBulkDueDate(null);
      setBulkDueDateAnchorEl(null);
    }
  }, [selectedActionItemIds.length]);

  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

  const dynamicHeight = isSmall
    ? "calc(80vh)"
    : isMedium
    ? "calc(85vh)"
    : "calc(80vh)";
  const dynamicMaxHeight = isSmall
    ? "calc(100vh - 200px)"
    : isMedium
    ? "calc(100vh - 250px)"
    : isLarge ? "calc(100vh - 200px)" : "calc(100vh - 200px)";

  const { mutate: mutateActionItemUpdate } =
    ActionItemService.useUpdateActionItem([actionItemToBeUpdated], {
      onMutate: () => {
        setShowSpinner(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["get_action_items"] });
        queryClient.invalidateQueries({
          queryKey: ["get_meetings_with_review_actions"],
        });
        if (actionItemToBeUpdated) clearHighlightForRow(actionItemToBeUpdated);
        showSnackbar("success", "Action item updated successfully.");
        setActionItemToBeUpdated("");
      },
      onError: (_error: string) => {
        showSnackbar("error", "Error while updating action item");
      },
      onSettled: () => {
        setShowSpinner(false);
        setActionItemToBeUpdated("");
      },
    });

  const { mutate: mutateActionItemDelete } =
    ActionItemService.useDeleteActionItem(actionItemToDelete?.id || "", {
      onMutate: () => {
        setShowSpinner(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["get_action_items"] });
        showSnackbar("success", "Action item deleted successfully");
      },
      onError: (error: string) => {
        showSnackbar("error", `Error while deleting action item: ${error}`);
      },
      onSettled: () => {
        handleConfirmationClose();
        setShowSpinner(false);
      },
    });

  useEffect(() => {
    setShowSpinner(isMeetingActionLoading || isMeetingActionFetching);
    if (isMeetingActionSuccess) {
      const responseData = MeetingActionItemData?.data?.data || {};
      const allActionItemList =
        selectedType === "external"
          ? responseData?.external
          : responseData?.internal;
      const sortedList = sortActionItems(allActionItemList);
      setRows(generateRows(sortedList, actionItemRowDetails, activeUsers));
    }
    if (isMeetingActionError) {
      const errorMessage =
        MeetingActionError?.response?.data?.errors ||
        "An unknown error occurred";
      showSnackbar("error", errorMessage);
    }
  }, [
    isMeetingActionLoading,
    isMeetingActionFetching,
    isMeetingActionSuccess,
    isMeetingActionError,
    MeetingActionItemData,
    actionItemRowDetails,
    activeUsers,
    selectedType,
  ]);

  const { mutate: mutateSubscribeActionItem } =
    ActionItemService.useSubscribeActionItem({
      onSuccess: (_response: any, variables: any) => {
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === variables.actionItemId
              ? { ...row, isSubscribed: variables.isSubscribed }
              : row
          )
        );
        const message = variables.isSubscribed
          ? "Successfully subscribed to action item."
          : "Successfully unsubscribed from action item.";
        showSnackbar("success", message);
      },
      onError: () => {
        showSnackbar("error", "Error while updating subscription.");
      },
    });

  const { mutate: mutateConvertActionItemToTask } =
    ActionItemService.useConvertActionItemToTask({
      onMutate: () => {
        setShowSpinner(true);
      },
      onSuccess: (response: any, variables: any) => {
        const message =
          response?.data?.message ||
          "Action items successfully converted to task.";
        showSnackbar("success", message);

        setRows((prevRows) =>
          patchConvertedActionItemRows(
            prevRows,
            variables.actionItemIds ?? [],
            variables.action === ConvertTaskAction.ACCEPTED
          )
        );
        setSelectedActionItemIds([]);
        queryClient.invalidateQueries({ queryKey: ["get_action_items"] });
      },

      onError: (error: any) => {
        const message =
          error?.response?.data?.message ||
          "Error while converting action item to task.";
        showSnackbar("error", message);
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    });


  const allRows = rows;   // show everything in one table

  const { mutateAsync: mutateBulkActionItemUpdateAsync } =
    ActionItemService.useUpdateActionItem(selectedActionItemIds, {
      onMutate: () => setShowSpinner(true),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["get_action_items"] });
        queryClient.invalidateQueries({
          queryKey: ["get_meetings_with_review_actions"],
        });

        showSnackbar("success", "Action items updated successfully.");
      },
      onError: () => {
        showSnackbar("error", "Error while updating action items");
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    });

  const bulkUpdatePromiseRef = useRef<Promise<unknown> | null>(null);

  const enqueueBulkUpdate = (payload: Record<string, any>) => {
    const previous = bulkUpdatePromiseRef.current ?? Promise.resolve();
    const promise = previous.then(
      () => mutateBulkActionItemUpdateAsync(payload),
      () => mutateBulkActionItemUpdateAsync(payload)
    );
    const tracked = promise.finally(() => {
      if (bulkUpdatePromiseRef.current === tracked) {
        bulkUpdatePromiseRef.current = null;
      }
    });
    bulkUpdatePromiseRef.current = tracked;
    return tracked;
  };

  const handleConvertToTask = (
    actionItemIds: string[],
    action: ConvertTaskAction,
  ) => {

    mutateConvertActionItemToTask({
      interactionId: id,
      actionItemIds,
      action,
    });
  };

  const handleOpenDiscardFeedback = (
    row: any,
    anchorEl?: HTMLElement | null
  ) => {
    const rowData = getRowData(row);
    const actionItemTitle = rowData?.description || "";
    const rowId = rowData?.id || row?.id;
    setFeedbackReference(actionItemTitle);
    setDiscardFeedbackAnchorEl(anchorEl ?? null);
    setDiscardFeedbackRowId(rowId);
    setRows((prevRows) =>
      prevRows.map((item) => ({
        ...item,
        isDiscardFeedbackActive: item.id === rowId,
      }))
    );
    setDiscardPopupOpen(true);
  };

  const validateActionItemsBeforeAccept = (actionItemRows: any[], autoOpen = true) => {
    const invalidRows = actionItemRows.filter((row) => {
      const rowData = getRowData(row);
      const assignee = rowData?.assignee;
      const dueDate = rowData?.dueDate || bulkDueDate;
      return !assignee || !dueDate;
    });

    if (invalidRows.length > 0) {
      invalidRows.forEach((row) => {
        const rowData = getRowData(row);
        const assignee = rowData?.assignee;
        const dueDate = rowData?.dueDate || bulkDueDate;
        highlightMissingFields(row, {
          assignee: !assignee,
          dueDate: !dueDate,
        }, autoOpen
      );
      });

      showSnackbar(
        "warning",
        "Please fill the missing Solo Player and Due Date before accepting."
      );
      return false;
    }

    return true;
  };

  const applyBulkSelectionToRows = () => {
    const assigneeEmail = bulkAssignee?.email ?? null;
    const assigneeName = bulkAssignee?.name ?? null;
    const assigneePorId = bulkAssignee?.profileOrgRelationshipId ?? bulkAssigneeId ?? null;
    const updates: Record<string, any> = {};

    if (assigneeEmail) {
      updates.assignee = assigneeEmail;
      updates.assignee_name = assigneeName;
      updates.assigneePorId = assigneePorId;
    }

    if (bulkDueDate) {
      updates.dueDate = bulkDueDate;
      updates.revised_due_date = bulkDueDate;
    }

    if (Object.keys(updates).length > 0) {
      patchSelectedRows(updates);
      selectedActionItemIds.forEach((rowId) => {
        if (updates.assignee) {
          removeHighlight(rowId, "assignee");
          removeHighlight(rowId, "open_assignee");
        }
        if (updates.dueDate) {
          removeHighlight(rowId, "dueDate");
          removeHighlight(rowId, "autoOpenDueDatePicker");
        }
      });
    }
  };

  const handleAcceptRows = (actionItemRows: any[]) => {
    if (!actionItemRows?.length) return;
    if (!validateActionItemsBeforeAccept(actionItemRows, true)) return;

    const actionItemIds = actionItemRows.map(
      (row) => row?.original?.id || row?.id
    );
    handleConvertToTask(actionItemIds, ConvertTaskAction.ACCEPTED);
  };

  const handleBulkAccept = async () => {
    if (!rowsEligibleForAcceptDiscard.length || isSubtaskBulkSelection) return;
    if (!validateActionItemsBeforeAccept(rowsEligibleForAcceptDiscard, false)) return;
    applyBulkSelectionToRows();
    const actionItemIds = rowsEligibleForAcceptDiscard.map((row) => row.id);
    try {
      if (bulkUpdatePromiseRef.current) {
        await bulkUpdatePromiseRef.current;
      }
    } catch {
      return;
    }
    handleConvertToTask(actionItemIds, ConvertTaskAction.ACCEPTED);
  };

  const handleDiscardRows = (actionItemRows: any[]) => {
    const rowsToDiscard = (actionItemRows || []).filter(isNoActionTaken);
    if (!rowsToDiscard.length) return;

    const actionItemIds = rowsToDiscard.map(
      (row) => row?.original?.id || row?.id
    );
    handleConvertToTask(actionItemIds, ConvertTaskAction.DISCARDED);
  };

  useEffect(() => {
    setColumns(
      getColumns(isLargeUp, {
        selectRowCallback: (row: any, canEdit: any, _isSubTask?: boolean) => {
          const inferredIsSubTask = resolveIsSubTaskFlag(row?.original ?? row);
          const item = {
            ...(row?.original ?? row),
            isSubTask: inferredIsSubTask,
          };
          canEdit
            ? handleActionItemSelection(item)
            : handleActionItemPreview(item);
        },
        deleteRowCallback: (row: any) => handleActionItemDelete(row.original),

        updateDueDateCallback: (row: any, value: string) => {
          setActionItemToBeUpdated(row.id);
          const formattedDate = formatDateOnly(value);
          setRows((prevRows) => {
            const patchTree = (list: any[]): any[] =>
              list.map((item) => {
                if (item.id === row.id) {
                  return {
                    ...item,
                    dueDate: formattedDate,
                    revised_due_date: formattedDate,
                  };
                }
                if (item.subRows) {
                  const nested = patchTree(item.subRows);
                  return { ...item, subRows: nested, subTasks: nested };
                }
                return item;
              });
            return patchTree(prevRows);
          });
          mutateActionItemUpdate({ dueDate: formattedDate });
        },

        updateAssigneeCallback: (row: any, value: string) => {
          setActionItemToBeUpdated(row.id);

          const updatePayload = { assigneePorId: value };

          mutateActionItemUpdate(updatePayload);
        },

        updateCoachCallback: (row: any, value: any) => {
          setActionItemToBeUpdated(row.id);

          const updatePayload = { coachPorId: value };

          mutateActionItemUpdate(updatePayload);
        },

        updatePriorityCallback: (row: any, value: string) => {
          setActionItemToBeUpdated(row.id);

          const updatePayload = { priority: value };

          mutateActionItemUpdate(updatePayload);
        },

        updateStatusCallback: (row: any, value: any) => {
          setActionItemToBeUpdated(row.id);

          const updatePayload = { status: value };

          mutateActionItemUpdate(updatePayload);
        },
        toggleSubscribeCallback: (row: any) => {
          mutateSubscribeActionItem({
            actionItemIds: [row.original.id],
            subscriberProfileOrgIdId: profileOrgId,
            isSubscribed: !row.original.isSubscribed,
          });
        },

        acceptCallback: (row: any) => {
          handleAcceptRows([row]);
        },

        discardCallback: (row: any) => {
          const actionItemId = row?.original?.id || row?.id;
          if (!actionItemId) return;

          handleDiscardRows([row]);
        },
        openDiscardFeedbackCallback: (row: any, anchorEl?: HTMLElement | null) =>
          handleOpenDiscardFeedback(row, anchorEl),
        highlightMissingFields,
        removeHighlight,
        showSnackbar,
        showFeedbackIcon: userFeedback,
        isBulkSelection: selectedActionItemIds?.length > 0,
      }, highlightedCells, removeHighlight, isImpersonating,)
    );
  }, [ highlightedCells, userFeedback, selectedActionItemIds.length, rows]);

  const handleDiscardSubmit = () => {
    showSnackbar("success", "Feedback submitted successfully.");
  };

  const handleActionItemSelection = (actionItem: any) => {
    setOpenDialog(true);
    setSelectedActionItem(actionItem);
  };

  const handleActionItemPreview = (actionItem: any) => {
    setOpenDialog(true);
    setSelectedActionItem(actionItem);
    setIsPreview(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedActionItem(null);
    setIsPreview(false);
    setIsNewActionItem?.(false);
    setBulkTasks?.(false);
  };

  const handleActionItemDelete = (actionItem: string) => {
    setActionItemToDelete(actionItem);
    setOpenConfirmation(true);
  };

  const handleConfirmationSuccess = () => {
    mutateActionItemDelete({ actionItemId: actionItemToDelete.id });
  };

  const handleConfirmationClose = () => {
    setOpenConfirmation(false);
    setActionItemToDelete(null);
  };

  const getActionItemRowProps = ({ row }: { row: any }) => {
    const isDiscardFeedbackActive = Boolean(
      row.original?.isDiscardFeedbackActive
    );

    return {
      className: isDiscardFeedbackActive ? "discard-row-active" : "",
      sx: {
        backgroundColor: "transparent",
        "& td, & th": {
          backgroundColor: isDiscardFeedbackActive ? "#FFF5F5" : "transparent",
        },
        "&:hover td, &:hover th": {
          backgroundColor: isDiscardFeedbackActive ? "#FFF5F5" : "#F2F2F4",
        },
      },
    };
  };

  return (
    <Box className="action-items-split">
      <Box>
        {selectedActionItemIds.length > 0 && (
          <Box className="bulk-update-bar">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
              <Box className="bulk-update-icons">
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#33333B",
                  }}
                >
                  {selectedActionItemIds.length} Task
                  {selectedActionItemIds.length > 1 ? "s" : ""} Selected
                </Typography>
                <Tooltip title="Clear selection" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedActionItemIds([]);
                      setBulkAssigneeId(null);
                      setBulkDueDate(null);
                      setBulkDueDateAnchorEl(null);
                    }}
                    sx={{
                      width: 20,
                      height: 20,
                    }}
                  >
                    <ClearOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box 
                className="inline-chip" 
                sx={{cursor: "pointer"}} 
                onClick={() => assigneeRef.current?.click()}
              >
                <UserSelector
                  triggerRef={assigneeRef}
                  value={bulkAssigneeId}
                  onChange={(value) => {
                    const nextValue = Array.isArray(value)
                      ? value[0] ?? null
                      : value;

                    setBulkAssigneeId(nextValue);

                    const selectedUser = activeUsers?.find(
                      (user) =>
                        user.profileOrgRelationshipId === nextValue ||
                        user.email === nextValue
                    );

                    patchSelectedRows({
                      assignee: selectedUser?.email,
                      assignee_name: selectedUser?.name,
                      assigneePorId: selectedUser?.profileOrgRelationshipId,
                    });

                    enqueueBulkUpdate({
                      assigneePorId: selectedUser?.profileOrgRelationshipId,
                    });
                  }}
                  users={activeUsers || []}
                  variant="icon"
                  variantIcon={AssigneeIcon}
                  tooltipTitle="Solo Player"
                />
                <Typography
                  sx={{
                    color: "#6F6F7F",
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  {bulkAssigneeId ? "" :"Solo Player"}
                </Typography>
              </Box>
              <Tooltip title="Due Date" placement="top" arrow>
                <Box
                  className="bulk-update-icons"
                  component="button"
                  type="button"
                  onClick={(e) => setBulkDueDateAnchorEl(e.currentTarget)}
                >
                  <img src={DueDateIcon} alt="Due Date" width={16} height={16} />
                  <span>
                    {bulkDueDate ? dayjs(bulkDueDate).format("MMM DD YYYY") : "Due Date"}
                  </span>
                </Box>
              </Tooltip>
              <CommonDatePicker
                value={bulkDueDate}
                onChange={(value) => {
                  const formattedDate = formatDateOnly(value);

                  setBulkDueDate(formattedDate);
                  setBulkDueDateAnchorEl(null);

                  patchSelectedRows({
                    dueDate: formattedDate,
                    revised_due_date: formattedDate,
                  });

                  enqueueBulkUpdate({
                    dueDate: formattedDate,
                  });
                }}
                onClose={() => setBulkDueDateAnchorEl(null)}
                minDate={meetingDate ? formatDateOnly(meetingDate) : undefined}
                anchorEl={bulkDueDateAnchorEl}
                saveAsDateOnly={true}
              />
            </Box>
            {showBulkAcceptDiscard && (
              <>
            <Box
              className="bulk-update-icons-accept-discard"
              component="button"
              type="button"
              onClick={handleBulkAccept}
              sx={{
                backgroundColor:"#E9FAF0",
                color: "#158F4C",
              }}
            >
              <DoneOutlinedIcon sx={{alignItems: "center", fontSize: "16px"}}/>
              Accept
            </Box>
            <Box
              className="bulk-update-icons-accept-discard"
              component="button"
              type="button"
              onClick={() => handleDiscardRows(selectedRows)}
              sx={{
                backgroundColor: "#FFF0F0",
                color: "#E84646",
              }}
            >
              <ClearOutlinedIcon sx={{alignItems: "center", fontSize: "16px"}}/>
              Discard
            </Box>
              </>
            )}
          </Box>
        )}
        {rows.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
              color: "#7A7A8C",
              fontSize: "14px",
            }}
          >
            No data available
          </Box>
        ) :!hasAnyDomainTopic ? (
          <BasicDatatable
            columns={columns}
            rows={allRows}
            rowCount={allRows.length}
            initialSort={initialSort}
            initialFilter={initialFilter}
            hidePaginationFooter={true}
            height={dynamicHeight}
            maxHeight={dynamicMaxHeight}
            minHeight="400px"
            isBordervisible={true}
            showSpinner={showSpinner}
            rowHoverEnabled={true}
            columnVisibility={{
              creator_name: false,
              subscribe: false,
              status: false,
              coachEmail: false,
              priority: false,
            }}
            enableRowSelection={true}
            selectedRowIds={selectedActionItemIds}
            onRowSelectionChange={handleBulkSelectionChange}
            checkboxStyle={{ size: "small", color: "#6036D6" }}
            disabledRowIds={flattenActionItemRows(allRows)
              .filter((row) => !row.canEdit|| isImpersonating)
              .map((row) => row.id)}
            hiddenSelectRowIds={getHiddenSelectRowIds(allRows)}
            muiTableBodyRowProps={getActionItemRowProps}
            enableExpanding={true}
            enableSubRowSelection={false}
            expanded={expandedRowIds}
            onExpandedChange={setExpandedRowIds}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", ml: 1.5 }}>
            {groupedRowsByDomainTopic.map((group) => (
              <Accordion
                key={group.key}
                expanded={expandedDomainTopic === group.key}
                onChange={(_e, expanded) =>
                  setExpandedDomainTopic(expanded ? group.key : false)
                }
                disableGutters
                elevation={0}
                square
                className="domain-topic-accordion"
              >
                <AccordionSummary
                  className="domain-topic-summary"
                  expandIcon={
                    <Box
                      component="img"
                      src={expandedDomainTopic === group.key ? CloseTaskIcon : OpenTaskIcon}
                      alt={expandedDomainTopic === group.key ? "Collapse" : "Expand"}
                      className="domain-topic-icon"
                    />
                  }
                >
                  <Box className="domain-topic-title-wrapper">
                    <Box className="domain-topic-title">
                      {group.label}
                      <span className="domain-topic-count">[{group.rows.length}]</span>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className="domain-topic-details">
                  <BasicDatatable
                    columns={columns}
                    rows={group.rows}
                    rowCount={group.rows.length}
                    initialSort={initialSort}
                    initialFilter={initialFilter}
                    hidePaginationFooter={true}
                    height="auto"
                    maxHeight="none"
                    minHeight="0px"
                    isBordervisible={true}
                    showSpinner={showSpinner}
                    rowHoverEnabled={true}
                    columnVisibility={{
                      creator_name: false,
                      subscribe: false,
                      status: false,
                      coachEmail: false,
                      priority: false,
                    }}
                    enableRowSelection={true}
                    selectedRowIds={selectedActionItemIds}
                    onRowSelectionChange={handleBulkSelectionChange}
                    checkboxStyle={{ size: "small", color: "#6036D6" }}
                    disabledRowIds={flattenActionItemRows(group.rows)
                      .filter((row) => !row.canEdit || isImpersonating)
                      .map((row) => row.id)}
                    hiddenSelectRowIds={getHiddenSelectRowIds(group.rows)}
                    muiTableBodyRowProps={getActionItemRowProps}
                    enableExpanding={true}
                    enableSubRowSelection={false}
                    expanded={expandedRowIds}
                    onExpandedChange={setExpandedRowIds}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
        <SlideOver
          isOpen={openDialog}
          onClose={handleClose}
          showHeaderButtons={isNewActionItem ? true : false}
          onSave={handleSave}
          onDiscard={handleDiscard}
          discard={selectedActionItem != null ? false : true}
          saveDisabled={!isActionItemValid}
          bulkTasks={bulkTasks}
          closeOnRight
        >
          <ActionItem
            actionItemType="MEETING_ACTION_ITEMS"
            attendees={attendees || []}
            meetingId={id}
            selectedActionItem={selectedActionItem}
            handleClose={handleClose}
            meetingDate={meetingDate}
            isPreview={isPreview}
            saveTrigger={saveTrigger}
            discardTrigger={discardTrigger}
            resetSaveTrigger={() => setSaveTrigger(false)}
            resetDiscardTrigger={() => setDiscardTrigger(false)}
            onValidityChange={setIsActionItemValid}
            setActionItemRowDetails={setActionItemRowDetails}
            isNewActionItem={isNewActionItem}
            openDialog={openDialog}
            tagsList={[]}
            spacesList={[]}
            bulkTasks={bulkTasks}
          />
        </SlideOver>
        <CustomConfirm
          title="Confirmation"
          message={"Are you sure you want to delete this action item?"}
          isOpen={openConfirmation}
          handleSuccess={handleConfirmationSuccess}
          handleClose={handleConfirmationClose}
          actionBtnLabel={"Confirm"}
        />
      </Box>
      <FeedbackPopup
        open={discardPopupOpen}
        interactionId={id}
        feedbackKey="action_item"
        feedbackReference={feedbackReference}
        anchorEl={discardFeedbackAnchorEl}
        onClose={clearDiscardFeedbackState}
        onSubmit={handleDiscardSubmit}
      />
    </Box>
  );
};

export default ActionItemsPage;
